import { useEffect, useCallback, useRef } from 'react';
import { eventBus, TaskEvents, TaskEventPayload } from '@/lib/eventBus';
import { supabase } from '@/integrations/supabase/client';
import { calculateStartTaskUpdates, calculateStopTaskUpdates, calculateToggleCompletionUpdates } from '@/logic/taskActions';
import { Task } from '@/types/task';
import { toast } from '@/lib/toast';
import { recordStateChange } from '@/hooks/useStateHistory';
import { formatLocalDate } from '@/lib/dateUtils';
import { enqueueEffect, getSyncQueue, removeEffect } from '@/lib/syncQueue';

/**
 * TaskEventHandler listens to global events and executes business logic.
 * It acts as the "Controller" in this architecture.
 */
export function TaskEventHandler() {
    const isProcessingRef = useRef(false);

    // Helper to fetch task if not provided in payload
    const getTask = async (payload: TaskEventPayload): Promise<Task | null> => {
        if (payload.task) return payload.task as Task;

        const { data, error } = await supabase
            .from('tasks')
            .select('*')
            .eq('id', payload.taskId)
            .maybeSingle();

        if (error || !data) {
            console.error('TaskEventHandler: failed to fetch task', error);
            return null;
        }

        // Simple mapping (should match your other mapper roughly)
        return {
            ...data,
            locationId: data.location_id,
            flowId: data.flow_id,
            flowOrder: data.flow_order,
            createdAt: new Date(data.created_at),
            timerStartedAt: data.timer_started_at ? new Date(data.timer_started_at) : null,
            actualDurationMinutes: data.actual_duration_minutes,
            // Add other date fields as needed by logic, mostly safe to date string for simple logic
        } as unknown as Task;
    };

    // Helper to commit to Supabase
    const applyUpdates = async (taskId: string, updates: Partial<Task>, successMsg?: string) => {
        const isOffline = !navigator.onLine;

        if (isOffline) {
            console.log('TaskEventHandler: Offline, queuing update', { taskId, updates });
            enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
            return;
        }

        try {
            const dbUpdates: any = {};
            // Map common fields to DB snake_case
            if ('status' in updates) dbUpdates.status = updates.status;
            if ('timerStartedAt' in updates) dbUpdates.timer_started_at = updates.timerStartedAt ? (updates.timerStartedAt as Date).toISOString() : null;
            if ('actualDurationMinutes' in updates) dbUpdates.actual_duration_minutes = updates.actualDurationMinutes;
            if ('completed' in updates) dbUpdates.completed = updates.completed;
            if ('lastCompletedDate' in updates) dbUpdates.last_completed_date = updates.lastCompletedDate ? (updates.lastCompletedDate as Date).toISOString() : null;
            if ('completedBy' in updates) dbUpdates.completed_by = updates.completedBy;
            if ('assignedTo' in updates) dbUpdates.assigned_to = updates.assignedTo;
            if ('dueDate' in updates) dbUpdates.due_date = updates.dueDate ? (updates.dueDate instanceof Date ? formatLocalDate(updates.dueDate) : (updates.dueDate as any)) : null;

            if (Object.keys(dbUpdates).length === 0) return;

            const { data: updatedTask, error } = await supabase
                .from('tasks')
                .update(dbUpdates)
                .eq('id', taskId)
                .select()
                .maybeSingle();

            if (error) {
                if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
                    enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
                    return;
                }
                throw error;
            }

            if (successMsg) {
                toast.success(successMsg, { channel: 'tasks' });
            }

            // Emit updated task so listeners (useTasks) can update local state
            if (updatedTask) {
                const updatedData = updatedTask as any;
                const mappedTask = {
                    ...updatedData,
                    createdAt: new Date(updatedData.created_at),
                    timerStartedAt: updatedData.timer_started_at ? new Date(updatedData.timer_started_at) : null,
                    actualDurationMinutes: updatedData.actual_duration_minutes,
                    completed: updatedData.completed,
                    lastCompletedDate: updatedData.last_completed_date ? new Date(updatedData.last_completed_date) : null,
                    triggerStateId: updatedData.trigger_state_id,
                    targetStateId: updatedData.target_state_id,
                    tagIds: updatedData.tag_ids || [],
                    // other fields...
                };
                eventBus.emit(TaskEvents.UPDATE, { taskId, task: mappedTask });
            }

        } catch (err) {
            console.error('TaskEventHandler error:', err);
            toast.error('Actie mislukt', { channel: 'tasks' });
            // Even on unknown error, we might want to queue it if it looks transient
            enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
        }
    };

    const processQueue = useCallback(async () => {
        if (isProcessingRef.current || !navigator.onLine) return;
        
        const queue = getSyncQueue();
        if (queue.length === 0) return;

        isProcessingRef.current = true;
        console.log(`TaskEventHandler: Processing ${queue.length} pending actions`);

        // Sort by timestamp to preserve order
        const sortedQueue = [...queue].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        for (const effect of sortedQueue) {
            try {
                if (effect.type === TaskEvents.UPDATE) {
                    const { taskId, updates, successMsg } = effect.payload;
                    
                    const dbUpdates: any = {};
                    if ('status' in updates) dbUpdates.status = updates.status;
                    if ('timerStartedAt' in updates) dbUpdates.timer_started_at = updates.timerStartedAt;
                    if ('actualDurationMinutes' in updates) dbUpdates.actual_duration_minutes = updates.actualDurationMinutes;
                    if ('completed' in updates) dbUpdates.completed = updates.completed;
                    if ('lastCompletedDate' in updates) dbUpdates.last_completed_date = updates.lastCompletedDate;
                    if ('completedBy' in updates) dbUpdates.completed_by = updates.completedBy;
                    if ('assignedTo' in updates) dbUpdates.assigned_to = updates.assignedTo;
                    if ('dueDate' in updates) dbUpdates.due_date = updates.dueDate;

                    const { error } = await supabase
                        .from('tasks')
                        .update(dbUpdates)
                        .eq('id', taskId);

                    if (error) throw error;
                    if (successMsg) toast.success(`Hersteld: ${successMsg}`, { channel: 'tasks' });
                } else if (effect.type === TaskEvents.CREATE) {
                    const { tempId, dbInsert, optimisticTask } = effect.payload;
                    const { data, error } = await supabase
                        .from('tasks')
                        .insert(dbInsert)
                        .select()
                        .maybeSingle();

                    if (error) throw error;
                    if (data) {
                        const realTask = { ...optimisticTask, id: data.id, createdAt: new Date(data.created_at) };
                        eventBus.emit(TaskEvents.CREATED, { taskId: tempId, task: realTask });
                    }
                }
                
                removeEffect(effect.id);
            } catch (err) {
                console.error('Failed to process sync effect:', effect, err);
                break; 
            }
        }

        isProcessingRef.current = false;
    }, []);

    useEffect(() => {
        const handleStart = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const updates = calculateStartTaskUpdates(task);
            await applyUpdates(task.id, updates, `Taak gestart: ${task.text}`);
        };

        const handleStop = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const updates = calculateStopTaskUpdates(task);
            await applyUpdates(task.id, updates, `Taak gestopt: ${task.text}`);
        };

        const handleToggle = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const result = calculateToggleCompletionUpdates(task);
            const updates = { ...result.updates, ...(result.extraUpdates || {}) };

            if (result.shouldToggle) {
                updates.completed = !task.completed;
                // Handle completion date
                if (updates.completed) {
                    updates.lastCompletedDate = new Date();
                    if (task.status === 'started') updates.status = 'pending';
                    // Record who completed it if available
                    if (payload.completedBy) {
                        updates.completedBy = [payload.completedBy];
                    }
                } else {
                    updates.lastCompletedDate = null;
                    updates.completedBy = null;
                }
            }

            await applyUpdates(task.id, updates);

            // Handle Flow State Transition
            if (updates.completed && task.flowId && task.locationId) {
                checkAndHandleFlowStateTransition(task.flowId, task.locationId);
            }

            // Handle state-transition automation (auto-transition on task completion)
            if (updates.completed) {
                checkAndHandleStateTransitionAutomation(task);
            }
        };

        const checkAndHandleFlowStateTransition = async (flowId: string, locationId: string) => {
            if (!navigator.onLine) return; 

            // 1. Fetch the flow and its transition rules
            const { data: flow, error: flowError } = await supabase
                .from('task_flows')
                .select('success_state_id')
                .eq('id', flowId)
                .single();

            const flowData = flow as any;
            if (flowError || !flowData || !flowData.success_state_id) return;

            // 2. Fetch current location state to verify transition source
            const { data: location, error: locError } = await supabase
                .from('tags')
                .select('*')
                .eq('id', locationId)
                .single();

            if (locError || !location) return;
            const actualCurrentStateId = (location as any).current_state_id;

            // 3. Verify 'From' state if specified
            if (flowData.from_state_id && flowData.from_state_id !== actualCurrentStateId) {
                console.log('TaskEventHandler: Skipping state transition. Current state does not match flow source state.');
                return;
            }

            // 4. Check if all tasks in this flow are completed
            const { data: tasks, error: tasksError } = await supabase
                .from('tasks')
                .select('completed')
                .eq('flow_id', flowId);

            if (tasksError || !tasks) return;

            const allCompleted = tasks.every(t => t.completed);

            if (allCompleted) {
                // 5. Update the location's state
                const { error: tagUpdateError } = await supabase
                    .from('tags')
                    .update({ current_state_id: flowData.success_state_id } as any)
                    .eq('id', locationId);

                if (tagUpdateError) {
                    console.error('Error updating location state:', tagUpdateError);
                    return;
                }

                // Record state history for flow completion
                const { data: tagTeam } = await supabase.from('tags').select('team_id').eq('id', locationId).single();
                if (tagTeam?.team_id) {
                    recordStateChange({
                        teamId: tagTeam.team_id,
                        tagId: locationId,
                        fromStateId: actualCurrentStateId || null,
                        toStateId: flowData.success_state_id,
                        triggerSource: 'flow',
                        triggerReference: flowId,
                    });
                }

                toast.success(`Status bijgewerkt naar: ${flowData.success_state_id}`, { channel: 'tasks' });
            }
        };

        const checkAndHandleStateTransitionAutomation = async (task: Task) => {
            if (!navigator.onLine) return;

            if (task.targetStateId) {
                const linkedTagId = task.tagIds?.[0] || (task as any).tagId;
                if (linkedTagId) {
                    const { data: tagData } = await supabase.from('tags').select('current_state_id, team_id').eq('id', linkedTagId).single();

                    const { error: updateError } = await supabase
                        .from('tags')
                        .update({ current_state_id: task.targetStateId })
                        .eq('id', linkedTagId);

                    if (!updateError) {
                        if (tagData?.team_id) {
                            recordStateChange({
                                teamId: tagData.team_id,
                                tagId: linkedTagId,
                                fromStateId: tagData.current_state_id || null,
                                toStateId: task.targetStateId,
                                triggerSource: 'task_completion',
                                triggerReference: task.id,
                            });
                        }
                        toast.success('Status automatisch bijgewerkt naar doelstatus', { channel: 'tasks' });
                        return;
                    }
                }
            }

            if (!task.notes) return;
            const match = task.notes.match(/Transitie: ([a-f0-9-]+)/);
            if (!match) return;

            const transitionId = match[1];

            const { data: transitionRow, error } = await (supabase
                .from('state_transitions' as any)
                .select('*')
                .eq('id', transitionId)
                .single() as any);

            if (error || !transitionRow) return;

            const { error: updateError } = await supabase
                .from('tags')
                .update({ current_state_id: transitionRow.to_state_id })
                .eq('id', transitionRow.tag_id);

            if (updateError) {
                console.error('Error auto-transitioning state:', updateError);
            } else {
                recordStateChange({
                    teamId: transitionRow.team_id,
                    tagId: transitionRow.tag_id,
                    fromStateId: transitionRow.from_state_id,
                    toStateId: transitionRow.to_state_id,
                    triggerSource: 'automation',
                    triggerReference: transitionId,
                });
                toast.success('Status automatisch bijgewerkt na voltooiing', { channel: 'tasks' });
            }
        };

        const handleCreate = async (payload: TaskEventPayload) => {
            if (!payload.task) return;

            const optimisticTask = payload.task as Task;
            const tempId = payload.taskId; 
            const payloadTeamId = (payload as any).teamId;

            if (!payloadTeamId) {
                console.error('TaskEventHandler: Missing teamId in CREATE payload');
                return;
            }

            const dbInsert: any = {
                team_id: payloadTeamId,
                text: optimisticTask.text,
                type: optimisticTask.type,
                priority: optimisticTask.priority,
                priority_value: optimisticTask.priorityValue,
                frequency: optimisticTask.frequency ?? 'onetime',
                status: 'pending',
                created_at: new Date().toISOString(),
                created_by: optimisticTask.createdBy || null,
                notes: optimisticTask.notes || null,
                image_url: optimisticTask.imageUrl || null,
                location_id: optimisticTask.locationId || null,
                assigned_to: optimisticTask.assignedTo || null,
                due_date: optimisticTask.dueDate ? formatLocalDate(optimisticTask.dueDate) : null,
                recurrence_interval: optimisticTask.recurrenceInterval || null,
                recurrence_value: optimisticTask.recurrenceValue || null,
                recurrence_days: optimisticTask.recurrenceDays || null,
                tag_id: (optimisticTask as any).tagId || null,
                tag_ids: optimisticTask.tagIds && optimisticTask.tagIds.length > 0 ? optimisticTask.tagIds : null
            };

            const isOffline = !navigator.onLine;
            if (isOffline) {
                enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('tasks')
                    .insert(dbInsert)
                    .select()
                    .maybeSingle();

                if (error) {
                    if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
                        enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
                        return;
                    }
                    throw error;
                }

                if (data) {
                    const realTask = {
                        ...optimisticTask,
                        id: data.id,
                        createdAt: new Date(data.created_at),
                    };

                    eventBus.emit(TaskEvents.CREATED, {
                        taskId: tempId, 
                        task: realTask
                    });

                    toast.success(`Taak aangemaakt: ${realTask.text}`, { channel: 'tasks' });
                }

            } catch (err) {
                console.error('Create task failed:', err);
                toast.error('Kon taak niet opslaan: ' + (err as Error).message, { channel: 'tasks' });
                enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
            }
        };

        eventBus.on(TaskEvents.START, handleStart);
        eventBus.on(TaskEvents.STOP, handleStop);
        eventBus.on(TaskEvents.TOGGLE, handleToggle);
        eventBus.on(TaskEvents.CREATE, handleCreate);

        window.addEventListener('online', processQueue);

        processQueue();

        return () => {
            eventBus.off(TaskEvents.START, handleStart);
            eventBus.off(TaskEvents.STOP, handleStop);
            eventBus.off(TaskEvents.TOGGLE, handleToggle);
            eventBus.off(TaskEvents.CREATE, handleCreate);
            window.removeEventListener('online', processQueue);
        };
    }, [processQueue]);

    return null; 
}
